/**
 * Error thrown when an API call fails; carries the HTTP status so the UI can
 * distinguish 403/404 responses (role-scoped API) from generic failures.
 */
export class HttpError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
  }
}

/** Build an HttpError from a failed fetch Response (reads the API error body). */
export async function httpError(res: Response): Promise<HttpError> {
  const text = await res.text().catch(() => '');
  try {
    const body = JSON.parse(text) as { ok?: false; error?: { message?: string; code?: string; details?: { fieldErrors?: Record<string, string[]> } } };
    const msg = body?.error?.message ?? body?.error?.code ?? res.statusText;
    const fields = body?.error?.details?.fieldErrors;
    const detail = fields
      ? Object.entries(fields).map(([k, v]) => `${k}: ${v.join(', ')}`).join('; ')
      : '';
    const full = detail ? `${msg} — ${detail}` : msg;
    return new HttpError(res.status, full || `Failed: ${res.status}`);
  } catch {
    return new HttpError(res.status, text || res.statusText || `Failed: ${res.status}`);
  }
}

export function isHttpStatus(error: unknown, ...statuses: number[]): boolean {
  return error instanceof HttpError && statuses.includes(error.status);
}
